import React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { Alert, Button, Dropdown, Modal, Table, Tag, Tooltip, Typography, message } from 'antd'
import moment from 'moment'
import classNames from 'classnames'
import { ColumnsType } from 'antd/es/table'
import { CheckCircleFilled, DownloadOutlined, InfoCircleOutlined, PaperClipOutlined, ReloadOutlined } from '@ant-design/icons'
import { ordersApi } from 'api/orders'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import {
  EOrderItemsStatus,
  EOrderStatus,
  ERecieveMethod,
  ECdekBarcodeFormat,
  IAdminGetOrderResponse,
  IOrderPromoDetails,
  IOrderItem
} from 'models/orders'
import { IMAGES_URL } from 'shared/constants/urls'
import { showServerError } from 'shared/utils/showServerError'
import { orderTags } from 'shared/constants/orders'
import { OrderDeadline } from 'components/orders/OrderDeadline/OrderDeadline'
import { OrderStatusHistory } from 'components/orders/OrderStatusHistory/OrderStatusHistory'
import { ListInfoItem } from 'components/orders/ListInfoItem/ListInfoItem'
import { CancelOrder } from 'components/orders/CancelOrder/CancelOrder'
import { DenyItemModal } from 'components/orders/DenyItemModal/DenyItemModal'
import { getOrderItemsColumns } from 'components/orders/OrderItemsColumns/getOrderItemsColumns'
import { ChangeShipmentPointButton, canChangeShipmentPoint } from 'components/orders/ChangeShipmentPoint/ChangeShipmentPoint'
import { ShipmentPointPicker } from 'components/orders/ChangeShipmentPoint/ShipmentPointPicker'
import { OrderModalLoader, OrderModalTitle } from 'components/orders/OrderModal/OrderModalParts'
import { getDeliveryTypeName } from 'shared/utils/orders/getDeliveryTypeName'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { getProductEditPath } from 'routing/utils/getProductEditPath'
import { adminRoutes } from 'shared/constants/routes'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { delay } from 'shared/utils/delay'
import { getDeliveryTrackingUrl } from 'shared/utils/orders/getDeliveryTrackingUrl'
import { showSellerDisabledModal } from 'shared/utils/orders/sellerDisabledModal'
import { getDeclination } from 'shared/utils/getDeclination'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { ERoles } from 'models/auth'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { useOrderShipFromAddress } from 'shared/hooks/useOrderShipFromAddress'
import { appSelectors } from 'store/app'
import styles from './Order.module.scss'
import type { IEmbeddedOrder } from 'components/orders/OrderModal/types'

// Кнопка «Отказаться» и раскрытие характеристик: клик по ним не должен открывать карточку товара
const INTERACTIVE_CELL_SELECTOR = '.ant-btn, .ant-collapse-header'

function formatDate(isoString: string) {
  const momentDate = moment(isoString)
  const formattedDate = momentDate.format('HH:mm')
  return formattedDate
}

const getDiscountPayerLabel = (promoDetails: IOrderPromoDetails) => {
  const isSellerPaid = (promoDetails.seller_discount_impact ?? 0) > 0
  const isStorePaid = (promoDetails.store_discount_impact ?? 0) > 0

  if (isSellerPaid && isStorePaid) {
    return 'продавец и магазин'
  }
  if (isSellerPaid) {
    return 'продавец'
  }
  if (isStorePaid) {
    return 'магазин'
  }
  if (promoDetails.discount_charge_type === 'SELLER') {
    return 'продавец'
  }
  if (promoDetails.discount_charge_type === 'STORE') {
    return 'магазин'
  }
  return '—'
}

interface IOrderProps {
  embedded?: IEmbeddedOrder
}

export const Order: React.FC<IOrderProps> = ({ embedded }) => {
  const params = useParams<{ id: string }>()
  const id = embedded?.orderId ?? params.id
  const navigate = useNavigate()
  const location = useLocation()
  const navigationState = location.state as { backPath?: string } | null
  const role = useAppSelector(appSelectors.selectRole)
  const isSuperAdmin = role === ERoles.ADMIN

  const [orderData, setOrderData] =
    React.useState<IAdminGetOrderResponse | null>(null)
  const [isLoaded, setIsLoaded] = React.useState(false)

  const [isChangeStatusLoading, setIsChangeStatusLoading] =
    React.useState<EOrderStatus | null>()
  const [denyItemId, setDenyItemId] = React.useState<string | null>(null)

  const [openCancelOrderModal, setOpenCancelOrderModal] = React.useState(false)
  const [isBarcodeLoading, setIsBarcodeLoading] = React.useState(false)
  const [isCdekRefreshing, setIsCdekRefreshing] = React.useState(false)
  const [isShipmentPointOpen, setIsShipmentPointOpen] = React.useState(false)

  React.useEffect(() => {
    getOrder()
  }, [])

  const showSuccessModal = () => {
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Заказ принят в работу',
      content:
        'Статус в заказе покупателя также сменится на статус “Принят в работу”',
      icon: <CheckCircleFilled style={{ color: '#52C41A' }} />,
      okText: 'Ок',
      cancelText: 'Закрыть',
      onOk() {
        bodyScrollLock.disable()
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const closeOrderCanceledModal = React.useCallback(() => {
    setOpenCancelOrderModal(false)
    bodyScrollLock.disable()
    getOrder()
  }, [])

  const openOrderCanceledModal = React.useCallback(() => {
    setOpenCancelOrderModal(true)
    bodyScrollLock.enable()
  }, [])

  const getOrder = async () => {
    try {
      message.loading('Загрузка данных о заказе')
      const response = await ordersApi.getOrder(id as string)
      setOrderData(response.data)
    } catch (e) {
      if (embedded) {
        showServerError(e)
        return embedded.onClose()
      }
      const error = e as AxiosError
      if (
        error.response?.status === 500 ||
        error.response?.status === 400 ||
        error.response?.status === 404
      ) {
        return navigate(adminRoutes.ORDERS)
      }
      showServerError(e)
    } finally {
      message.destroy()
      setIsLoaded(true)
    }
  }

  const removeProductFromOrder = (productId: string) => {
    setDenyItemId(productId)
    bodyScrollLock.enable()
  }

  // Ошибку не гасим: модалка ждёт исключения, чтобы показать ответ ЮKassa
  const confirmDenyItem = async (count?: number) => {
    const { data } = await ordersApi.removeProduct(orderData?.order._id as string, {
      item_id: denyItemId as string,
      count
    })
    showSellerDisabledModal(data.seller_penalty)
  }

  const closeDenyItemModal = () => {
    setDenyItemId(null)
    bodyScrollLock.disable()
    getOrder()
  }

  // Всё количество единственного товара — отказ от заказа. Блокировку прокрутки снимет окно отказа
  const switchToCancelOrder = () => {
    setDenyItemId(null)
    setOpenCancelOrderModal(true)
  }

  const changeStatus = async (status: EOrderStatus, cancelReason?: string) => {
    try {
      setIsChangeStatusLoading(status)
      await ordersApi.changeStatus({
        status,
        orderId: orderData?.order._id as string,
        reason_of_cancel: cancelReason
      })
      await delay(3000)
      getOrder()
      if (status === EOrderStatus.CONFIRMED_BY_SELLER) {
        showSuccessModal()
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsChangeStatusLoading(null)
    }
  }

  // Ошибку не гасим: модалка ждёт исключения, чтобы показать ответ сервера
  const confirmCancelOrder = async (cancelReason: string) => {
    const { data } = await ordersApi.changeStatus({
      status: EOrderStatus.CANCELED,
      orderId: orderData?.order._id as string,
      reason_of_cancel: cancelReason
    })
    showSellerDisabledModal(data.seller_penalty)
  }

  const goBack = () => {
    navigate(navigationState?.backPath ?? adminRoutes.ORDERS)
  }

  const downloadDeliveryLabel = async (format: ECdekBarcodeFormat) => {
    try {
      setIsBarcodeLoading(true)
      const response = await ordersApi.getDeliveryLabel(orderData?.order._id as string, format)
      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `delivery-label-${orderData?.order.number}-${format}.pdf`
      link.click()
      window.URL.revokeObjectURL(blobUrl)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsBarcodeLoading(false)
    }
  }

  const refreshCdekStatus = async () => {
    try {
      setIsCdekRefreshing(true)
      const { data } = await ordersApi.refreshCdekStatus(orderData?.order._id as string)
      if (data.registrationError) {
        message.warning('СДЭК по-прежнему не принял накладную')
      } else {
        message.success('Данные по доставке обновлены')
      }
      await getOrder()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsCdekRefreshing(false)
    }
  }

  const agreedItems = React.useMemo(
    () =>
      orderData?.order.items.filter(
        (item) => item.items_status === EOrderItemsStatus.AGREED
      ) ?? [],
    [orderData]
  )

  const columns: ColumnsType<IOrderItem> = React.useMemo(() => {
    const baseColumns = getOrderItemsColumns()
    // Последний товар из заказа целиком не убираем: возврат за него частичный, заказ остался бы
    // без товаров и без возврата доставки — для этого есть отказ от всего заказа. Часть единиц можно
    const canDenyItems =
      agreedItems.length > 1 || agreedItems.some((item) => item.count > 1)

    if (
      canDenyItems &&
      orderData?.order.status === EOrderStatus.ON_CONFIRMATION &&
      orderData.order.receive_method !== ERecieveMethod.EXPRESS
    ) {
      baseColumns.push({
        title: 'Убрать товар',
        dataIndex: '',
        width: 190,
        render: (_, { _id }) => {
          return (
            <Button
              danger
              htmlType='button'
              size='middle'
              loading={denyItemId === _id}
              onClick={() => removeProductFromOrder(_id)}
            >
              Отказаться
            </Button>
          )
        }
      })
    }
    return baseColumns
  }, [orderData, denyItemId, agreedItems])

  const denyItem = agreedItems.find((item) => item._id === denyItemId) ?? null

  const shipFromAddress = useOrderShipFromAddress(orderData?.order)

  if (!isLoaded || !orderData) {
    return embedded ? <OrderModalLoader /> : null
  }

  // Пояснение показываем, пока у продавца остаются отмены за 30 дней: крон сводит
  // cancel_count к нулю, а надбавка в самом заказе остаётся навсегда
  const sellerCancelCount = orderData.order.seller_cancel_count ?? 0
  const isCommissionRaisedForSeller =
    !isSuperAdmin &&
    !!orderData.order.commission_penalty_rate &&
    !!orderData.order.seller_id?.cancel_count &&
    sellerCancelCount > 0
  const deliveryProvider =
    orderData.order.delivery_info?.provider ?? orderData.order.pvz?.provider
  const isYandexDelivery = deliveryProvider === 'YANDEX'
  const deliveryTitle = getDeliveryTypeName(
    orderData.order.receive_method,
    deliveryProvider,
    orderData.order.pvz?.name
  )
  const deliveryTrackingUrl = getDeliveryTrackingUrl(
    orderData.order.delivery_info,
    deliveryProvider
  )
  const deliveryError =
    orderData.order.delivery_info?.error ??
    orderData.order.delivery_info?.cdek_error
  const isPvzDelivery = orderData.order.receive_method === ERecieveMethod.PVZ
  const isOwnDelivery = orderData.order.receive_method === ERecieveMethod.OWN_DELIVERY
  const buyerDeliverySum = isPvzDelivery
    ? orderData.order.pvz?.delivery_sum ?? 0
    : orderData.order.receive_method === ERecieveMethod.PICKUP_POINT
      ? 0
      : orderData.order.delivery_info?.delivery_sum ?? 0
  // Остаток доставки до ПВЗ сверх доли покупателя закрывает площадка или продавец.
  // Долю площадки продавцу не раскрываем, поэтому полную стоимость видит только тот,
  // кто сам доплатил часть
  const sellerDeliverySum =
    isPvzDelivery && orderData.order.isFreeDeliveryToPvz === false
      ? orderData.order.discount_pvz_delivery_sum ?? 0
      : 0

  return (
    <>
      <div style={{ paddingBottom: embedded ? 0 : 60 }}>
        <div className={classNames(styles.header, embedded && styles.headerInModal)}>
          {embedded ? (
            <OrderModalTitle orderId={orderData.order._id} number={orderData.order.number} />
          ) : (
            <MainTitle
              isGoBack
              onGoBack={goBack}
              text={`Заказ № ${orderData.order.number}`}
              style={{ marginBottom: 0 }}
            />
          )}
          <div className={styles.statusWrapper}>
            <Tag
              color={orderTags[orderData.order.status].color}
              className={styles.status}
            >
              {orderTags[orderData.order.status].label}
            </Tag>
            <OrderDeadline order={orderData.order} />
          </div>
        </div>
        <Table
          scroll={{
            x: 1100
          }}
          columns={columns}
          dataSource={agreedItems}
          rowKey={(el) => el._id}
          pagination={false}
          style={{ marginBottom: 30 }}
          onRow={({ product_id }) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).closest(INTERACTIVE_CELL_SELECTOR)) return
              if (product_id?._id) {
                window.open(getProductEditPath(product_id._id), '_blank')
              }
            },
            style: { cursor: product_id?._id ? 'pointer' : 'default' }
          })}
        />
        <div className={styles.detailsContainer}>
          <div className={styles.detailsWrapper} style={{ width: orderData.order.delivery_info ? '100%' : '66.6666%' }}>
            {orderData.order.delivery_info && (
              <div
                className={classNames(
                  styles.detailsItem,
                  styles.columnWithLine
                )}
              >
                <Typography.Title className={styles.detailsItemTitle} level={3}>
                  {deliveryTitle}
                </Typography.Title>
                <ListInfoItem
                  name='Вес заказа'
                  value={`${(orderData.order.weight / 1000).toFixed(1)} кг`}
                />
                {orderData.order.delivery_info?.yandex_offer?.price && <ListInfoItem
                  name='Стоимость доставки'
                  value={`${orderData.order.delivery_info?.yandex_offer?.price.total_price} ₽`}
                />}
                {orderData.order.delivery_info?.yandex_offer?.delivery_interval && <ListInfoItem
                  name='Время доставки'
                  value={`${formatDate(orderData.order.delivery_info?.yandex_offer?.delivery_interval.from)} - ${formatDate(orderData.order.delivery_info?.yandex_offer?.delivery_interval.to)}`}
                />}
                {orderData.order.delivery_info?.tracking && (
                  <ListInfoItem
                    name='Отслеживание'
                    value={orderData.order.delivery_info.tracking}
                  />
                )}
                {orderData.order.delivery_info?.invoice && (
                  <div className={styles.invoiceWrapper}>
                    <Typography.Text style={{ flexShrink: 0 }}>Накладная:</Typography.Text>
                    <div className={styles.invoice}>
                      <PaperClipOutlined
                        style={{ opacity: 0.45, fontSize: 22 }}
                      />
                      <Typography.Link
                        href={`${IMAGES_URL}${orderData.order.delivery_info.invoice}`}
                        target='_blank'
                      >
                        накладная.pdf
                      </Typography.Link>
                    </div>
                  </div>
                )}
                {deliveryTrackingUrl && (
                  <Button
                    htmlType='button'
                    href={deliveryTrackingUrl}
                    target='_blank'
                    className={styles.deliveryActionButton}
                    style={{ marginTop: 15 }}
                  >
                    Отследить заказ
                  </Button>
                )}
                {deliveryError && (
                  <Alert
                    type='error'
                    showIcon
                    style={{ marginTop: 15 }}
                    message={`Ошибка создания доставки ${isYandexDelivery ? 'Яндекс' : 'СДЭК'}`}
                    description={`${deliveryError.replace(/\.*$/, '')}. Обратитесь в поддержку.`}
                    action={!isYandexDelivery ? (
                      <Button
                        size='small'
                        htmlType='button'
                        icon={<ReloadOutlined />}
                        loading={isCdekRefreshing}
                        onClick={refreshCdekStatus}
                      >
                        Обновить
                      </Button>
                    ) : undefined}
                  />
                )}
                {isYandexDelivery &&
                  (orderData.order.delivery_info?.external_id ||
                    orderData.order.delivery_info?.label) && (
                  <Button
                    htmlType='button'
                    icon={<DownloadOutlined />}
                    loading={isBarcodeLoading}
                    onClick={() => downloadDeliveryLabel(ECdekBarcodeFormat.A6)}
                    className={styles.deliveryActionButton}
                    style={{ marginTop: 15 }}
                  >
                    Скачать штрихкод
                  </Button>
                )}
                {!deliveryError &&
                  orderData.order.status === EOrderStatus.CONFIRMED_BY_SELLER &&
                  orderData.order.delivery_info?.provider !== 'YANDEX' &&
                  (orderData.order.delivery_info?.external_id ||
                    orderData.order.delivery_info?.cdek_uuid) && (
                  <Dropdown
                    menu={{
                      items: Object.values(ECdekBarcodeFormat).map((format) => ({
                        key: format,
                        label: format
                      })),
                      onClick: ({ key }) => downloadDeliveryLabel(key as ECdekBarcodeFormat)
                    }}
                    trigger={['click']}
                  >
                    <Button
                      htmlType='button'
                      icon={<DownloadOutlined />}
                      loading={isBarcodeLoading}
                      className={styles.deliveryActionButton}
                      style={{ marginTop: 15 }}
                    >
                      Скачать штрихкод
                    </Button>
                  </Dropdown>
                )}
              </div>
            )}
            <div className={classNames(styles.detailsItem, { [styles.shippingInfoWithoutCdek]: !orderData.order.delivery_info })} style={{ width: orderData.order.delivery_info ? 'calc(100% / 3)' : 'calc(100% / 2)' }}>
              <Typography.Title className={styles.detailsItemTitle} level={3}>
                Информация о доставке
              </Typography.Title>
              <ListInfoItem
                name='Способ доставки'
                value={getDeliveryTypeName(
                  orderData.order.receive_method,
                  deliveryProvider,
                  orderData.order.pvz?.name
                )}
                style={{ gap: 15 }}
                valueStyle={{ textAlign: 'left' }}
              />
              {!!orderData.order.preparation_date && (
                <ListInfoItem
                  name='Подготовка до'
                  value={moment(orderData.order.preparation_date).format(
                    'DD.MM.YYYY'
                  )}
                  style={{ gap: 35 }}
                  valueStyle={{ textAlign: 'left' }}
                />
              )}
              <ListInfoItem
                name={(orderData.order.receive_method === ERecieveMethod.COURIER || orderData.order.receive_method === ERecieveMethod.EXPRESS || isOwnDelivery) ? 'Адрес доставки' : orderData.order.receive_method === ERecieveMethod.PVZ ? 'Доставка до' : 'Самовывоз из'}
                value={
                  orderData.order?.delivery_info?.address?.address ??
                  orderData.order?.pvz?.location?.address_full ??
                  orderData.order?.shop_id?.address?.name
                }
                style={{ gap: (orderData.order.receive_method === ERecieveMethod.COURIER || isOwnDelivery) ? 25 : orderData.order.receive_method === ERecieveMethod.PVZ ? 49 : 37 }}
                valueStyle={{ textAlign: 'left' }}
              />
              {/* Везёт сам продавец — без контактов ему не договориться о времени */}
              {isOwnDelivery && !!orderData.order.delivery_info?.full_name && (
                <ListInfoItem
                  name='Получатель'
                  value={orderData.order.delivery_info.full_name}
                  style={{ gap: 25 }}
                  valueStyle={{ textAlign: 'left' }}
                />
              )}
              {isOwnDelivery && !!orderData.order.delivery_info?.tel && (
                <ListInfoItem
                  name='Телефон'
                  value={phoneMaskStatic(orderData.order.delivery_info.tel)}
                  style={{ gap: 25 }}
                  valueStyle={{ textAlign: 'left' }}
                />
              )}
              {isOwnDelivery && !!orderData.order.delivery_info?.comment && (
                <ListInfoItem
                  name='Комментарий'
                  value={orderData.order.delivery_info.comment}
                  style={{ gap: 25 }}
                  valueStyle={{ textAlign: 'left' }}
                />
              )}
              {shipFromAddress && (
                <ListInfoItem
                  name='Отправка из'
                  value={
                    <>
                      {shipFromAddress}
                      <ChangeShipmentPointButton
                        order={orderData.order}
                        onClick={() => setIsShipmentPointOpen((prev) => !prev)}
                      />
                    </>
                  }
                  style={{ gap: 49 }}
                  valueStyle={{ textAlign: 'left' }}
                />
              )}
            </div>
            <div
              className={classNames(
                styles.detailsItem,
                styles.priceInfoContainer
              )}
              style={{ width: orderData.order.delivery_info ? 'calc(100% / 3)' : 'calc(100% / 2)' }}
            >
              <div className={styles.priceInfoWrapper}>
                <Typography.Title className={styles.detailsItemTitle} level={3}>
                  Стоимость
                </Typography.Title>
                {orderData.order.promo_details?.is_applied && (
                  <ListInfoItem
                    name='Промокод'
                    value={
                      <Tag color='green' style={{ marginRight: 0 }}>
                        {orderData.order.promo_details.promo_code ?? '—'}
                      </Tag>
                    }
                  />
                )}
                <ListInfoItem
                  name='Стоимость товаров'
                  value={getNormalizedPrice(orderData.order.sum)}
                />
                {sellerDeliverySum > 0 && (
                  <ListInfoItem
                    name='Стоимость доставки'
                    value={getNormalizedPrice(buyerDeliverySum + sellerDeliverySum)}
                  />
                )}
                <ListInfoItem
                  name='Комиссия площадки'
                  value={
                    <>
                      {`${!!orderData.commission ? '-' : ''}${getNormalizedPrice(orderData.commission)}`}
                      {isCommissionRaisedForSeller && (
                        <Tooltip
                          title={`На момент заказа за последние 30 дней у вас было ${sellerCancelCount} ${getDeclination(sellerCancelCount % 100, ['отмен', 'отмена', 'отмены'])}, поэтому комиссия магазина увеличена. Каждая отмена учитывается 30 дней, все отмены обнуляются после 10 заказов подряд без отмен`}
                        >
                          <InfoCircleOutlined style={{ marginLeft: 6 }} />
                        </Tooltip>
                      )}
                    </>
                  }
                />
                {isSuperAdmin && !!orderData.commission && !!orderData.order.penalty_commission && (
                  <ListInfoItem
                    name='Повышенная комиссия'
                    value={`-${getNormalizedPrice(orderData.order.penalty_commission)}`}
                  />
                )}
                {orderData.order.promo_details?.is_applied && (
                  <>
                    <ListInfoItem
                      name='Цена до скидки'
                      value={getNormalizedPrice(
                        orderData.order.promo_details.original_sum ?? 0
                      )}
                    />
                    <ListInfoItem
                      name='Цена после скидки'
                      value={getNormalizedPrice(
                        orderData.order.promo_details.discounted_sum ?? 0
                      )}
                    />
                    <ListInfoItem
                      name='Скидка'
                      value={getNormalizedPrice(
                        orderData.order.promo_details.discount_amount ?? 0
                      )}
                    />
                    <ListInfoItem
                      name='Скидку оплатил'
                      value={getDiscountPayerLabel(
                        orderData.order.promo_details
                      )}
                    />
                  </>
                )}
              </div>
              <div className={styles.finalPrice}>
                <Typography.Title
                  level={4}
                  style={{ marginBottom: 0, marginTop: 0 }}
                >
                  Итог:
                </Typography.Title>
                <Typography.Title
                  level={4}
                  style={{ marginBottom: 0, marginTop: 0 }}
                >
                  {getNormalizedPrice(orderData.order.full_sum)}
                </Typography.Title>
              </div>
              <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                <ListInfoItem
                  name='Доставка для покупателя'
                  value={getNormalizedPrice(buyerDeliverySum)}
                />
                {sellerDeliverySum > 0 && (
                  <ListInfoItem
                    name='Доставка для селлера'
                    value={getNormalizedPrice(sellerDeliverySum)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {isShipmentPointOpen && canChangeShipmentPoint(orderData.order) && (
          <div className={styles.detailsContainer}>
            <ShipmentPointPicker
              key={orderData.order._id}
              order={orderData.order}
              currentAddress={shipFromAddress}
              onClose={() => setIsShipmentPointOpen(false)}
              onChanged={getOrder}
            />
          </div>
        )}
        <div className={styles.detailsContainer}>
          <div className={classNames(styles.detailsWrapper, styles.historyWrapper)}>
            <div className={classNames(styles.detailsItem, styles.historyItem)}>
              <Typography.Title className={styles.detailsItemTitle} level={3}>
                История заказа
              </Typography.Title>
              <OrderStatusHistory order={orderData.order} />
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          {orderData.order.status === EOrderStatus.CONFIRMED_BY_SELLER &&
            orderData.order.receive_method === ERecieveMethod.PICKUP_POINT && (
            <Button
              size='large'
              htmlType='button'
              type='primary'
              loading={
                isChangeStatusLoading === EOrderStatus.WAITING_IN_POINT
              }
              onClick={() => changeStatus(EOrderStatus.WAITING_IN_POINT)}
            >
              Ожидает получения
            </Button>
          )}
          {orderData.order.status === EOrderStatus.WAITING_IN_POINT && orderData.order.receive_method === ERecieveMethod.PICKUP_POINT && (
            <Button
              size='large'
              htmlType='button'
              type='primary'
              loading={
                isChangeStatusLoading === EOrderStatus.FINISHED
              }
              onClick={() => changeStatus(EOrderStatus.FINISHED)}
            >
              Заказ передан клиенту
            </Button>
          )}
          {orderData.order.status === EOrderStatus.CONFIRMED_BY_SELLER && isOwnDelivery && (
            <Button
              size='large'
              htmlType='button'
              type='primary'
              loading={
                isChangeStatusLoading === EOrderStatus.IN_DELIVERY
              }
              onClick={() => changeStatus(EOrderStatus.IN_DELIVERY)}
            >
              Заказ в пути
            </Button>
          )}
          {orderData.order.status === EOrderStatus.IN_DELIVERY && isOwnDelivery && (
            <Button
              size='large'
              htmlType='button'
              type='primary'
              loading={
                isChangeStatusLoading === EOrderStatus.FINISHED
              }
              onClick={() => changeStatus(EOrderStatus.FINISHED)}
            >
              Заказ передан клиенту
            </Button>
          )}
          {orderData.order.status === EOrderStatus.ON_CONFIRMATION && orderData.order.receive_method !== ERecieveMethod.EXPRESS && (
            <>
              <Button
                size='large'
                htmlType='button'
                type='primary'
                loading={
                  isChangeStatusLoading === EOrderStatus.CONFIRMED_BY_SELLER
                }
                onClick={() => changeStatus(EOrderStatus.CONFIRMED_BY_SELLER)}
              >
                Принять в работу
              </Button>
              <Button
                danger
                size='large'
                htmlType='button'
                type='primary'
                onClick={openOrderCanceledModal}
              >
                Отказаться от заказа
              </Button>
            </>
          )}
        </div>
      </div>
      <CancelOrder
        isOpen={openCancelOrderModal}
        onConfirm={confirmCancelOrder}
        close={closeOrderCanceledModal}
      />
      <DenyItemModal
        item={denyItem}
        isLastItem={agreedItems.length === 1}
        onConfirm={confirmDenyItem}
        onCancelOrder={switchToCancelOrder}
        onClose={closeDenyItemModal}
        isSuperAdmin={isSuperAdmin}
      />
    </>
  )
}
